import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import api, { inr } from "../lib/api";
import { Heart, LogOut, Package, MapPin, Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EMPTY_ADDR = { label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "", country: "India", isDefault: false };

export default function Account() {
    const { user, logout } = useAuth();
    const { items: wishlistItems } = useWishlist();
    const [orders, setOrders] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [addrEdit, setAddrEdit] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "My Account | Loomara";
        api.get("/orders/mine").then(({ data }) => setOrders(data));
        api.get("/addresses").then(({ data }) => setAddresses(data));
    }, []);

    const saveAddress = async () => {
        try {
            if (addrEdit.id) {
                const { data } = await api.put(`/addresses/${addrEdit.id}`, addrEdit);
                setAddresses((cur) => cur.map((a) => (a.id === data.id ? data : a)));
                toast("Address updated");
            } else {
                const { data } = await api.post("/addresses", addrEdit);
                setAddresses((cur) => [...cur, data]);
                toast("Address saved");
            }
            setAddrEdit(null);
        } catch (e) { toast(e.response?.data?.detail || "Save failed"); }
    };
    const removeAddress = async (a) => {
        if (!window.confirm("Delete this address?")) return;
        await api.delete(`/addresses/${a.id}`);
        setAddresses((cur) => cur.filter((x) => x.id !== a.id));
        toast("Address deleted");
    };

    return (
        <div className="container-boutique py-16" data-testid="account-page">
            <p className="eyebrow">My Account</p>
            <h1 className="font-serif text-5xl text-ink-900 mt-3">Hi, {user?.name?.split(" ")[0]}</h1>
            <p className="text-ink-500 mt-2">{user?.email}</p>

            <Tabs defaultValue="orders" className="mt-10">
                <TabsList className="bg-ivory-100 rounded-full p-1 inline-flex">
                    <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full text-xs uppercase tracking-[0.18em] px-5 py-2 data-[state=active]:bg-sage-400 data-[state=active]:text-white">Orders</TabsTrigger>
                    <TabsTrigger value="addresses" data-testid="tab-addresses" className="rounded-full text-xs uppercase tracking-[0.18em] px-5 py-2 data-[state=active]:bg-sage-400 data-[state=active]:text-white">Addresses</TabsTrigger>
                    <TabsTrigger value="wishlist" data-testid="tab-wishlist" className="rounded-full text-xs uppercase tracking-[0.18em] px-5 py-2 data-[state=active]:bg-sage-400 data-[state=active]:text-white">Wishlist</TabsTrigger>
                </TabsList>

                {/* ORDERS */}
                <TabsContent value="orders" className="mt-8">
                    {orders.length === 0 ? (
                        <div className="card-soft p-12 text-center">
                            <Package size={28} className="mx-auto text-sage-400" strokeWidth={1.2} />
                            <p className="font-serif text-2xl text-ink-900 mt-3">No orders yet</p>
                            <p className="text-ink-500 mt-2">When you place your first order, it'll appear here.</p>
                            <Link to="/shop" className="btn-primary mt-6 inline-flex">Start Browsing</Link>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {orders.map((o) => (
                                <Link to={`/order/${o.id}`} key={o.id} className="card-soft p-6 block hover:shadow-[0_10px_40px_rgba(168,184,154,0.18)] transition" data-testid={`order-${o.orderNumber}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-4">
                                        <div>
                                            <p className="font-serif text-lg text-ink-900">Order #{o.orderNumber}</p>
                                            <p className="text-xs text-ink-500 mt-1 uppercase tracking-[0.18em]">{o.status} · {o.paymentStatus}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sage-700 font-medium">{inr(o.total)}</p>
                                            <p className="text-xs text-ink-500 mt-1">{o.items.length} item{o.items.length === 1 ? "" : "s"}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 mt-4">
                                        {o.items.slice(0, 5).map((i, idx) => (
                                            <div key={idx} className="h-14 w-12 rounded-lg overflow-hidden bg-ivory-100">
                                                <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* ADDRESSES */}
                <TabsContent value="addresses" className="mt-8">
                    <div className="flex justify-end mb-4">
                        <button onClick={() => setAddrEdit({ ...EMPTY_ADDR })} className="btn-primary" data-testid="new-address-btn"><Plus size={14} /> New Address</button>
                    </div>
                    {addresses.length === 0 ? (
                        <div className="card-soft p-12 text-center">
                            <MapPin size={28} className="mx-auto text-sage-400" strokeWidth={1.2} />
                            <p className="font-serif text-2xl text-ink-900 mt-3">No saved addresses</p>
                            <p className="text-ink-500 mt-2">Save an address to make checkout faster.</p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-4">
                            {addresses.map((a) => (
                                <div key={a.id} className="card-soft p-5" data-testid={`address-${a.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="font-medium text-ink-900">{a.label} {a.isDefault && <span className="text-[10px] uppercase tracking-[0.22em] text-sage-600 ml-2">Default</span>}</p>
                                            <p className="text-sm text-ink-500 mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</p>
                                            <p className="text-xs text-ink-500 mt-1">📞 {a.phone}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setAddrEdit(a)} className="text-sage-700 hover:text-sage-600" aria-label="Edit"><Edit3 size={15} /></button>
                                            <button onClick={() => removeAddress(a)} className="text-blush-400 hover:text-blush-500" aria-label="Delete"><Trash2 size={15} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* WISHLIST */}
                <TabsContent value="wishlist" className="mt-8">
                    <div className="card-soft p-8">
                        <Heart size={22} className="text-sage-600 mb-3" />
                        <p className="font-serif text-2xl text-ink-900">{wishlistItems.length === 0 ? "Nothing saved yet" : `${wishlistItems.length} ${wishlistItems.length === 1 ? "piece" : "pieces"} saved`}</p>
                        <p className="text-sm text-ink-500 mt-2">Tap the heart on any piece to save it for later.</p>
                        <Link to="/wishlist" className="btn-secondary mt-5 inline-flex">View Wishlist</Link>
                    </div>
                </TabsContent>
            </Tabs>

            <div className="mt-12">
                <button onClick={async () => { await logout(); navigate("/"); }} className="text-xs uppercase tracking-[0.22em] text-ink-500 hover:text-ink-900 inline-flex items-center gap-2" data-testid="logout-btn">
                    <LogOut size={14} /> Sign out
                </button>
            </div>

            {/* Address modal */}
            {addrEdit && (
                <div className="fixed inset-0 z-[70] bg-ink-900/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif text-2xl">{addrEdit.id ? "Edit Address" : "New Address"}</h2>
                            <button onClick={() => setAddrEdit(null)}><X /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                ["label", "Label", 1], ["phone", "Phone", 1],
                                ["line1", "Address line 1", 2], ["line2", "Address line 2", 2],
                                ["city", "City", 1], ["state", "State", 1],
                                ["pincode", "Pincode", 1], ["country", "Country", 1],
                            ].map(([k, label, span]) => (
                                <div key={k} className={span === 2 ? "col-span-2" : ""}>
                                    <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">{label}</label>
                                    <input value={addrEdit[k] || ""} onChange={(e) => setAddrEdit({ ...addrEdit, [k]: e.target.value })} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid={`addr-${k}`} />
                                </div>
                            ))}
                            <label className="col-span-2 inline-flex items-center gap-2 text-sm text-ink-700 mt-2">
                                <input type="checkbox" checked={!!addrEdit.isDefault} onChange={(e) => setAddrEdit({ ...addrEdit, isDefault: e.target.checked })} />
                                Default address
                            </label>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setAddrEdit(null)} className="btn-secondary">Cancel</button>
                            <button onClick={saveAddress} className="btn-primary" data-testid="addr-save-btn">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
